import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import RefreshBar from '@/components/ui/RefreshBar'
import TitleRow from '@/components/ui/TitleRow'
import { useSuspenseMarketOverview } from '@/features/market-overview/api/queries'
import BreadthBar from '@/features/market-overview/components/BreadthBar'
import IndicesRankTable from '@/features/market-overview/components/IndicesRankTable'
import MoversPair from '@/features/market-overview/components/MoversPairCard'
import OverviewIndexPanel from '@/features/market-overview/components/OverviewIndexPanel'
import TopBrokersCard from '@/features/market-overview/components/TopBrokersCard'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { useRefreshState } from '@/hooks/useRefreshState'
import { MarketBoardSkeleton } from '@/screens/market/components/MarketBoardStates'
import { loadMarketOverview } from '@/screens/market/OverviewScreen.loader'
import TodaysBriefCard from '@/screens/app/home/components/TodaysBriefCard'

/**
 * Known without data, so every state draws the same one. The day's headline
 * is the index panel's own first line (the mock's band, 2026-10-28).
 */
const TITLE = 'Overview'
const HEADER = <TitleRow title={TITLE} />

/**
 * The first load draws one page-wide skeleton, never nine per-card ones: every
 * block on the tab reads the same overview query, so they would all be
 * pending together anyway. The skeleton is shaped like the LOADED board, not
 * like V2's Loading frame (`4236:96362`) — the owner's call, so the page does
 * not grow by a screen's height the moment the data lands.
 */
function OverviewPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the market board…"
      header={HEADER}
      pageTitle={TITLE}
    >
      <MarketBoardSkeleton />
    </ScreenLayout>
  )
}

/**
 * V2's failed board (`4236:96619`), in the design's own words. No frame
 * captures Market's offline copy, so that kind keeps EmptyState's shared
 * default rather than an invented sentence.
 */
function OverviewError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load the market board"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market')({
  loader: loadMarketOverview,
  pendingComponent: OverviewPending,
  errorComponent: OverviewError,
  component: OverviewScreen,
})

/**
 * Market · Overview — what NEPSE did today, top to bottom.
 *
 * The screen reads the overview once, as a suspense query, and hands the
 * blocks that only draw figures their slice of it; the index panel and the
 * movers pair still call `useMarketOverview` for themselves (TanStack dedupes
 * on the key). Under the loader they all mount with
 * the data already cached, so none of them ever shows its own skeleton — the
 * first load is the route's pending page, and a first-load failure is the
 * route's error page. Once the board is drawn, a failed or in-flight refetch
 * keeps it on screen and speaks through the bar below.
 */
function OverviewScreen() {
  const query = useSuspenseMarketOverview()
  // Not `query.refetch()` on its own. This screen's `queryFn` fans out to ten
  // raw endpoints through the endpoint cache, and inside that cache's 10s
  // freshness window a bare refetch re-enters the fan-out, fetches nothing and
  // still moves `dataUpdatedAt` — so Retry printed "Updated just now" over
  // byte-identical data for up to 10s after every poll tick. `useManualRefresh`
  // invalidates the endpoint entries first, and carries the "a member asked for
  // this" flag `useRefreshState` needs to tell a Retry from a background
  // refetch. See its own doc comment for both halves.
  const manual = useManualRefresh([query])
  const refresh = useRefreshState([query], manual.isManual)

  // V2's loaded frame has no freshness strip at all, and its "Refreshing"
  // frame (`4236:97302`) draws one only while the refetch runs. So the bar
  // mounts for refreshing / failed / offline and stays out otherwise — the
  // standing "Updated just now" line the loaded page used to carry is gone.
  // This trades RefreshBar's reserve-the-slot rule for the frame: the page
  // does move down by the bar's height when a member-started refresh begins.
  // That is the frame's own layout, and it only happens on a click.
  const barState = refresh.state === 'updated' ? null : refresh.state

  const { data } = query

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      {barState === null ? null : (
        <RefreshBar
          state={barState}
          {...(refresh.message === undefined ? {} : { message: refresh.message })}
          onRetry={manual.refresh}
          className="-mt-2 h-9"
        />
      )}

      <OverviewIndexPanel />

      {/* Home's brief card, as the mock repeats it here: the same figures-only story until
          mm-be writes one (`BRIEF_API_AVAILABLE`), and the same reader behind it. */}
      <TodaysBriefCard />

      <BreadthBar breadth={data.breadth} />

      <MoversPair />

      <IndicesRankTable indices={data.indexTiles} />

      <TopBrokersCard brokers={data.floorsheetHighlights} />
    </ScreenLayout>
  )
}
